/*
  Graph Merge Utilities

  Functions for merging workflow graphs into journey graphs when expanding
  workflows. Handles namespacing, positioning, and container creation.
*/

import type { Node, Edge } from 'react-flow-renderer';
import type { ExpansionBounds } from './state';
import {
  WORKFLOW_CONTAINER_HEADER_HEIGHT as HEADER_HEIGHT,
  WORKFLOW_CONTAINER_PADDING as CONTAINER_PADDING,
} from '../components/nodes/workflow/containerConstants';

// ============================================================================
// Constants
// ============================================================================

const MIN_CONTAINER_WIDTH = 600;
const MIN_CONTAINER_HEIGHT = 400;
const DEFAULT_NODE_WIDTH = 200;
const DEFAULT_NODE_HEIGHT = 50;
const OUTCOME_SPACING = 100;
const OUTCOME_HORIZONTAL_OFFSET = 100;

// ============================================================================
// Namespacing Functions
// ============================================================================

/**
 * Namespace a workflow node ID to prevent collision with journey nodes.
 * Format: wf::{workflowToken}::{nodeId}
 */
export function namespaceNodeId(workflowToken: string, nodeId: string): string {
  return `wf::${workflowToken}::${nodeId}`;
}

/**
 * Namespace a workflow edge ID to prevent collision with journey edges.
 * Format: wfe::{workflowToken}::{edgeId}
 */
export function namespaceEdgeId(workflowToken: string, edgeId: string): string {
  return `wfe::${workflowToken}::${edgeId}`;
}

// ============================================================================
// Node Transformation
// ============================================================================

/**
 * Transform a workflow node for embedding in the journey canvas.
 *
 * This function:
 * - Namespaces the React Flow node ID to prevent collisions
 * - Sets parentNode to the container for React Flow subflow grouping
 * - Adjusts positions to account for container header and padding
 * - Preserves original node ID for workflow components to access Redux state
 * - Marks outcome nodes for auto-layout (unlike workflow editor's fixed island)
 *
 * @param node - Original workflow node
 * @param workflowToken - Token identifying the workflow
 * @param containerNodeId - ID of the container node this will be parented to
 * @returns Transformed node ready for embedding
 */
export function transformWorkflowNode(
  node: Node,
  workflowToken: string,
  containerNodeId: string,
): Node {
  const originalId = node.id;
  const isOutcomeNode = node.type === 'outcome';

  // Outcome nodes: Convert from fixed island layout to auto-layout for embedded view
  const nodeData = isOutcomeNode ? {
    ...node.data,
    layout: {
      ...node.data?.layout,
      fixed: false,
      autoLayout: true,
      float: false,
    },
  } : node.data;

  return {
    ...node,
    id: namespaceNodeId(workflowToken, node.id),
    position: {
      // Offset position to account for container header and padding
      x: (node.position?.x ?? 0) + CONTAINER_PADDING,
      y: (node.position?.y ?? 0) + HEADER_HEIGHT + CONTAINER_PADDING,
    },
    parentNode: containerNodeId,
    extent: 'parent' as const,
    data: {
      ...nodeData,
      workflowToken,
      isEmbedded: true,
      originalWorkflowNodeId: originalId,
    },
    hidden: false,
    draggable: false,
  };
}

/**
 * Transform a workflow edge for embedding in the journey canvas.
 *
 * Namespaces the edge ID and updates source/target to match transformed nodes.
 */
export function transformWorkflowEdge(
  edge: Edge,
  workflowToken: string,
): Edge {
  return {
    ...edge,
    id: namespaceEdgeId(workflowToken, edge.id),
    source: namespaceNodeId(workflowToken, edge.source),
    target: namespaceNodeId(workflowToken, edge.target),
    data: {
      ...edge.data,
      workflowToken,
      isEmbedded: true,
    },
    style: {
      ...edge.style,
    },
  };
}

// ============================================================================
// Position Normalization
// ============================================================================

/**
 * Normalize node positions to start from origin (0, 0).
 *
 * Workflow nodes come with absolute positions from the workflow editor.
 * For embedding, we need to normalize them relative to the container's origin.
 *
 * Outcome nodes are handled separately and positioned to the right of
 * other nodes in a vertical column.
 */
function normalizeWorkflowNodePositions(workflowNodes: Node[]): Node[] {
  const nonOutcomeNodes = workflowNodes.filter(n => n.type !== 'outcome');
  const outcomeNodes = workflowNodes.filter(n => n.type === 'outcome');

  // Find minimum positions from non-outcome nodes
  const minX = nonOutcomeNodes.length > 0
    ? Math.min(...nonOutcomeNodes.map(n => n.position?.x ?? 0))
    : 0;
  const minY = nonOutcomeNodes.length > 0
    ? Math.min(...nonOutcomeNodes.map(n => n.position?.y ?? 0))
    : 0;

  // Normalize non-outcome nodes to origin
  const normalizedNonOutcomeNodes = nonOutcomeNodes.map(node => ({
    ...node,
    position: {
      x: (node.position?.x ?? 0) - minX,
      y: (node.position?.y ?? 0) - minY,
    },
  }));

  // Position outcome nodes in a column to the right of other nodes
  const maxX = normalizedNonOutcomeNodes.length > 0
    ? Math.max(...normalizedNonOutcomeNodes.map(
      n => (n.position?.x ?? 0) + DEFAULT_NODE_WIDTH,
    ))
    : 0;

  const normalizedOutcomeNodes = outcomeNodes.map((node, index) => ({
    ...node,
    position: {
      x: maxX + OUTCOME_HORIZONTAL_OFFSET,
      y: index * OUTCOME_SPACING,
    },
  }));

  return [...normalizedNonOutcomeNodes, ...normalizedOutcomeNodes];
}

// ============================================================================
// Bounds Calculation
// ============================================================================

/**
 * Calculate bounding box dimensions for a set of nodes.
 *
 * Returns the width and height needed to contain all visible nodes
 * in the normalized coordinate space.
 */
function calculateNodesBounds(nodes: Node[]): { width: number; height: number } {
  if (nodes.length === 0) {
    return { width: MIN_CONTAINER_WIDTH, height: MIN_CONTAINER_HEIGHT };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach(node => {
    if (node.hidden) return;

    const x = node.position?.x ?? 0;
    const y = node.position?.y ?? 0;
    const width = (node.style?.width as number) ?? DEFAULT_NODE_WIDTH;
    const height = (node.style?.height as number) ?? DEFAULT_NODE_HEIGHT;

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  });

  return {
    width: maxX - minX,
    height: maxY - minY,
  };
}

// ============================================================================
// Container Creation
// ============================================================================

/**
 * Create an expanded workflow container node.
 *
 * This replaces the collapsed workflow node with an expanded container
 * that will hold the embedded workflow graph as child nodes.
 */
function createExpandedContainerNode(
  containerNode: Node,
  workflowToken: string,
  containerWidth: number,
  containerHeight: number,
): Node {
  return {
    id: containerNode.id,
    type: 'expandedWorkflowContainer' as const,
    position: { x: 0, y: 0 }, // Layout algorithm will position correctly
    data: {
      ...containerNode.data,
      workflowToken,
      workflowVersion: containerNode.data?.config?.workflow_version,
      isExpanded: true,
      isJourneyContainerForWorkflow: true,
      width: containerWidth,
      height: containerHeight,
    },
    style: {
      width: containerWidth,
      height: containerHeight,
      zIndex: 0,
    },
    draggable: false,
    className: 'workflow-container-group',
  };
}

// ============================================================================
// Main Merge Function
// ============================================================================

/**
 * Merge workflow graph into journey graph.
 *
 * Takes a journey graph and a workflow graph and combines them into a unified
 * graph where the workflow nodes are embedded inside an expanded container.
 *
 * Process:
 * 1. Find the workflow node in the journey that should become the container
 * 2. Normalize workflow node positions to start from (0, 0)
 * 3. Calculate container size needed to hold all workflow nodes
 * 4. Transform workflow nodes with namespacing and positioning
 * 5. Create the expanded container node
 * 6. Combine everything into final graph
 *
 * @param journeyNodes - Nodes from the journey graph
 * @param journeyEdges - Edges from the journey graph
 * @param workflowNodes - Nodes from the workflow graph
 * @param workflowEdges - Edges from the workflow graph
 * @param workflowToken - Token identifying the workflow to merge
 * @param bounds - Expansion bounds (currently unused but kept for compatibility)
 * @returns Combined graph with workflow embedded in journey
 */
export function mergeWorkflowIntoJourney(
  journeyNodes: Node[],
  journeyEdges: Edge[],
  workflowNodes: Node[],
  workflowEdges: Edge[],
  workflowToken: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  bounds: ExpansionBounds,
): { nodes: Node[]; edges: Edge[] } {
  // Find the workflow node that will become the container
  const containerNode = journeyNodes.find(
    node => (node.type === 'workflowNode' || node.type === 'workflow')
      && node.data?.config?.workflow_token === workflowToken,
  );

  if (!containerNode) {
    // eslint-disable-next-line no-console
    console.error('Container node not found for workflow token:', workflowToken);
    return { nodes: journeyNodes, edges: journeyEdges };
  }

  const containerNodeId = containerNode.id;

  // Normalize workflow node positions to start from origin
  const normalizedNodes = normalizeWorkflowNodePositions(workflowNodes);

  // Calculate container size based on normalized workflow bounds
  const workflowBounds = calculateNodesBounds(normalizedNodes);
  const containerWidth = Math.max(
    workflowBounds.width + (CONTAINER_PADDING * 2),
    MIN_CONTAINER_WIDTH,
  );
  const containerHeight = Math.max(
    workflowBounds.height + HEADER_HEIGHT + (CONTAINER_PADDING * 2),
    MIN_CONTAINER_HEIGHT,
  );

  // Transform workflow nodes and edges for embedding
  const transformedNodes = normalizedNodes.map(
    node => transformWorkflowNode(node, workflowToken, containerNodeId),
  );
  const transformedEdges = workflowEdges.map(
    edge => transformWorkflowEdge(edge, workflowToken),
  );

  // Create the expanded container node
  const groupContainerNode = createExpandedContainerNode(
    containerNode,
    workflowToken,
    containerWidth,
    containerHeight,
  );

  // Remove the original workflow node and add the expanded container
  const filteredJourneyNodes = journeyNodes.filter(
    node => !((node.type === 'workflowNode' || node.type === 'workflow')
      && node.data?.config?.workflow_token === workflowToken),
  );

  const finalNodes = [
    ...filteredJourneyNodes,
    groupContainerNode,
    ...transformedNodes,
  ];

  return {
    nodes: finalNodes,
    edges: [...journeyEdges, ...transformedEdges],
  };
}
